import { useState } from 'react';
import { apiClient } from '../../../auth-lib';
import { Button, Card, Input, Modal, EmptyState, useToast } from '../../../design-system';

const EMPTY = { name: '', din: '', pan: '', aadhaar: '', role: '' };

// Directors / partners / authorized signatories. DSCs for these people are
// managed separately in the Expirables tab (type = DSC, owner = this person).
function SignatoryManager({ clientId, signatories, onChange }) {
  const toast = useToast();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (field, v) => setForm((f) => ({ ...f, [field]: v }));

  const openAdd = () => { setEditing(null); setForm(EMPTY); setError(''); setModalOpen(true); };
  const openEdit = (s) => {
    setEditing(s._id);
    setForm({ name: s.name || '', din: s.din || '', pan: s.pan || '', aadhaar: s.aadhaar || '', role: s.role || '' });
    setError('');
    setModalOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError('');
    try {
      const res = editing
        ? await apiClient.put(`/api/clients/${clientId}/signatories/${editing}`, form)
        : await apiClient.post(`/api/clients/${clientId}/signatories`, form);
      onChange(res.data);
      setModalOpen(false);
      toast.success(editing ? 'Signatory updated' : 'Signatory added');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save signatory');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (signatoryId) => {
    if (!window.confirm('Remove this signatory? Their DSC records will also be removed.')) return;
    try {
      const res = await apiClient.delete(`/api/clients/${clientId}/signatories/${signatoryId}`);
      onChange(res.data);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to remove signatory');
    }
  };

  return (
    <Card>
      <Card.Header title="Signatories / Directors" action={<Button size="sm" onClick={openAdd}>+ Add Signatory</Button>} />
      {signatories.length === 0 ? (
        <EmptyState icon="🖊️" title="No signatories added" description="Add directors, partners, or authorized signatories to track their DSCs and DIR-3 KYC." />
      ) : (
        <div className="gstins-list">
          {signatories.map((s) => (
            <div key={s._id} className="gstin-card">
              <div className="gstin-info">
                <strong>{s.name}</strong>
                {s.role && <span className="gstin-meta">{s.role}</span>}
                {s.din && <span className="gstin-meta">DIN: {s.din}</span>}
                {s.pan && <span className="gstin-meta">PAN: {s.pan}</span>}
              </div>
              <div className="enrollment-actions">
                <Button variant="secondary" size="sm" onClick={() => openEdit(s)}>Edit</Button>
                <button onClick={() => remove(s._id)} className="btn-remove">Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Edit Signatory' : 'Add Signatory'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalOpen(false)} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={save} loading={saving}>{editing ? 'Save' : 'Add'}</Button>
          </>
        }
      >
        <form className="client-form" onSubmit={save}>
          {error && <div className="form-error" role="alert">{error}</div>}
          <div className="form-row">
            <Input label="Name *" value={form.name} onChange={(e) => set('name', e.target.value)} />
            <Input label="Role" value={form.role} onChange={(e) => set('role', e.target.value)} placeholder="Director / Partner / Signatory" />
          </div>
          <div className="form-row">
            <Input label="DIN" value={form.din} onChange={(e) => set('din', e.target.value)} maxLength={8} />
            <Input label="PAN" value={form.pan} onChange={(e) => set('pan', e.target.value.toUpperCase())} maxLength={10} />
            <Input label="Aadhaar" value={form.aadhaar} onChange={(e) => set('aadhaar', e.target.value)} maxLength={12} />
          </div>
        </form>
      </Modal>
    </Card>
  );
}

export default SignatoryManager;
